import React, { useState, useEffect, useCallback } from 'react';
import { Search, ChevronLeft, ChevronRight, Plus, Trash2, Edit2, ToggleLeft, ToggleRight, MapPin, X } from 'lucide-react';
import {
  fetchDeliverablePostcodes,
  fetchDeliverablePostcodesCount,
  createDeliverablePostcode,
  updateDeliverablePostcode,
  toggleDeliverablePostcodeActive,
  deleteDeliverablePostcode,
  fetchCountriesForDropdown,
  fetchStatesByCountry,
  DeliverablePostcode,
} from '../services/api.ts';
import './DeliverablePostcodes.css';

interface CountryOption { id: number; name: string; code: string; }
interface StateOption { id: number; name: string; code: string; }

interface FormData {
  postcode: string;
  town: string;
  state_id: number | '';
  country_id: number | '';
  latitude: string;
  longitude: string;
  is_active: boolean;
}

const emptyForm: FormData = {
  postcode: '',
  town: '',
  state_id: '',
  country_id: '',
  latitude: '',
  longitude: '',
  is_active: true,
};

const DeliverablePostcodes: React.FC = () => {
  const [postcodes, setPostcodes] = useState<DeliverablePostcode[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 20;

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Dropdown data
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [states, setStates] = useState<StateOption[]>([]);
  const [loadingStates, setLoadingStates] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const offset = (currentPage - 1) * perPage;
      const [list, count] = await Promise.all([
        fetchDeliverablePostcodes(offset, perPage),
        fetchDeliverablePostcodesCount(),
      ]);
      setPostcodes(list);
      setTotal(count);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    fetchCountriesForDropdown().then(setCountries).catch(console.error);
  }, []);

  // Load states when country changes in form
  useEffect(() => {
    if (form.country_id) {
      setLoadingStates(true);
      fetchStatesByCountry(Number(form.country_id))
        .then(setStates)
        .catch(console.error)
        .finally(() => setLoadingStates(false));
    } else {
      setStates([]);
    }
  }, [form.country_id]);

  const filtered = postcodes.filter((p) => {
    const s = `${p.postcode} ${p.town} ${p.state?.name} ${p.country?.name}`.toLowerCase();
    return s.includes(searchTerm.toLowerCase());
  });

  const totalPages = Math.ceil(total / perPage);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormError(null);
    setShowModal(true);
  };

  const openEdit = (p: DeliverablePostcode) => {
    setEditingId(p.id);
    setForm({
      postcode: p.postcode,
      town: p.town,
      state_id: p.state.id,
      country_id: p.country.id,
      latitude: p.latitude != null ? String(p.latitude) : '',
      longitude: p.longitude != null ? String(p.longitude) : '',
      is_active: p.is_active,
    });
    setFormError(null);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.postcode.trim() || !form.town.trim() || !form.state_id || !form.country_id) {
      setFormError('Postcode, Town, Country and State are required');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const payload: any = {
        postcode: form.postcode.trim(),
        town: form.town.trim(),
        state_id: Number(form.state_id),
        country_id: Number(form.country_id),
        latitude: form.latitude ? parseFloat(form.latitude) : null,
        longitude: form.longitude ? parseFloat(form.longitude) : null,
        is_active: form.is_active,
      };
      if (editingId) {
        await updateDeliverablePostcode(editingId, payload);
      } else {
        await createDeliverablePostcode(payload);
      }
      setShowModal(false);
      loadData();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setFormError(
        Array.isArray(detail)
          ? detail.map((d: any) => d.msg || JSON.stringify(d)).join('; ')
          : typeof detail === 'string' ? detail : 'Failed to save'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id: number) => {
    try {
      await toggleDeliverablePostcodeActive(id);
      loadData();
    } catch (err) {
      console.error('Toggle failed:', err);
    }
  };

  const handleDelete = async (id: number, postcode: string) => {
    if (!window.confirm(`Delete postcode "${postcode}"?`)) return;
    try {
      await deleteDeliverablePostcode(id);
      loadData();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  if (loading) {
    return (
      <div className="dp-page">
        <div className="dp-loading"><div className="dp-spinner" /><p>Loading deliverable postcodes...</p></div>
      </div>
    );
  }

  return (
    <div className="dp-page">
      {/* Header */}
      <div className="dp-header">
        <div className="dp-header-left">
          <MapPin size={24} />
          <h1>Deliverable Postcodes</h1>
          <span className="dp-count">{total}</span>
        </div>
        <button className="dp-btn dp-btn-primary" onClick={openAdd}>
          <Plus size={16} /> Add Postcode
        </button>
      </div>

      {/* Search */}
      <div className="dp-search-bar">
        <Search size={18} />
        <input
          type="text"
          placeholder="Search by postcode, town, state or country..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {error && <div className="dp-error">{error}</div>}

      {/* Table */}
      <div className="dp-table-wrap">
        <table className="dp-table">
          <thead>
            <tr>
              <th>Postcode</th>
              <th>Town</th>
              <th>State</th>
              <th>Country</th>
              <th>Lat / Lng</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={7} className="dp-empty">No deliverable postcodes found</td></tr>
            ) : (
              filtered.map((p) => (
                <tr key={p.id} className={!p.is_active ? 'dp-row-inactive' : ''}>
                  <td className="dp-cell-postcode">{p.postcode}</td>
                  <td>{p.town}</td>
                  <td>{p.state?.name || '—'}</td>
                  <td>{p.country?.name || '—'}</td>
                  <td className="dp-cell-coords">
                    {p.latitude != null && p.longitude != null
                      ? `${p.latitude.toFixed(4)}, ${p.longitude.toFixed(4)}`
                      : '—'}
                  </td>
                  <td>
                    <button
                      className={`dp-toggle ${p.is_active ? 'dp-toggle-on' : 'dp-toggle-off'}`}
                      onClick={() => handleToggle(p.id)}
                      title={p.is_active ? 'Active — click to deactivate' : 'Inactive — click to activate'}
                    >
                      {p.is_active ? <ToggleRight size={22} /> : <ToggleLeft size={22} />}
                      <span>{p.is_active ? 'Active' : 'Inactive'}</span>
                    </button>
                  </td>
                  <td className="dp-cell-actions">
                    <button className="dp-action-btn dp-action-edit" onClick={() => openEdit(p)} title="Edit">
                      <Edit2 size={15} />
                    </button>
                    <button className="dp-action-btn dp-action-delete" onClick={() => handleDelete(p.id, p.postcode)} title="Delete">
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="dp-pagination">
          <button disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
            <ChevronLeft size={16} /> Prev
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
            Next <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="dp-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="dp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dp-modal-header">
              <h2>{editingId ? 'Edit Postcode' : 'Add Deliverable Postcode'}</h2>
              <button className="dp-modal-close" onClick={() => setShowModal(false)}><X size={20} /></button>
            </div>
            <div className="dp-modal-body">
              {formError && <div className="dp-form-error">{formError}</div>}

              <div className="dp-form-row">
                <label>Postcode *</label>
                <input
                  type="text"
                  value={form.postcode}
                  onChange={(e) => setForm({ ...form, postcode: e.target.value })}
                  placeholder="e.g. 3000"
                />
              </div>

              <div className="dp-form-row">
                <label>Town *</label>
                <input
                  type="text"
                  value={form.town}
                  onChange={(e) => setForm({ ...form, town: e.target.value })}
                  placeholder="e.g. Melbourne CBD"
                />
              </div>

              <div className="dp-form-row">
                <label>Country *</label>
                <select
                  value={form.country_id}
                  onChange={(e) => setForm({ ...form, country_id: e.target.value ? Number(e.target.value) : '', state_id: '' })}
                >
                  <option value="">Select Country</option>
                  {countries.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div className="dp-form-row">
                <label>State *</label>
                <select
                  value={form.state_id}
                  onChange={(e) => setForm({ ...form, state_id: e.target.value ? Number(e.target.value) : '' })}
                  disabled={!form.country_id || loadingStates}
                >
                  <option value="">{loadingStates ? 'Loading...' : 'Select State'}</option>
                  {states.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>

              <div className="dp-form-row dp-form-row-half">
                <div>
                  <label>Latitude</label>
                  <input
                    type="text"
                    value={form.latitude}
                    onChange={(e) => setForm({ ...form, latitude: e.target.value })}
                    placeholder="Auto-detected if empty"
                  />
                </div>
                <div>
                  <label>Longitude</label>
                  <input
                    type="text"
                    value={form.longitude}
                    onChange={(e) => setForm({ ...form, longitude: e.target.value })}
                    placeholder="Auto-detected if empty"
                  />
                </div>
              </div>

              <div className="dp-form-row dp-form-check">
                <label>
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  />
                  Active
                </label>
              </div>
            </div>
            <div className="dp-modal-footer">
              <button className="dp-btn dp-btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="dp-btn dp-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliverablePostcodes;
